/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectCustomAcousticModels,
  selectCustomLanguageModels,
  selectTextToSpeechInstanceLoaded
} from '../../../store/voice/selectors';
import { NARROWBAND, BROADBAND, TELEPHONY } from '../constants';
import { FIELDS } from '../schema';
import { ControlledDropdownSelector } from '../speech-common/ControlledDropdownSelector';
import { SpeechToText as messages } from './messages';

export const CustomModels = () => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const bandPreference = getFieldValue(FIELDS.STT.BAND_PREFERENCE);
  const sttInstanceId = getFieldValue(FIELDS.STT.INSTANCE_ID);
  const sttNarrowbandModel = getFieldValue(FIELDS.STT.NARROWBAND.MODEL);
  const sttBroadbandModel = getFieldValue(FIELDS.STT.BROADBAND.MODEL);
  const sttInstanceURl = getFieldValue(FIELDS.STT.URL);

  const [customLanguageField, setCustomLanguageField] = useState(
    FIELDS.STT.NARROWBAND.LANGUAGE_CUSTOMIZATION_ID
  );
  const [customAcousticField, setCustomAcousticField] = useState(
    FIELDS.STT.NARROWBAND.ACCOUSTIC_CUSTOMIZATION_ID
  );
  const allCustomLanguageModels = useAppSelector(
    selectCustomLanguageModels,
    shallowEqual
  );
  const allCustomAcousticModels = useAppSelector(
    selectCustomAcousticModels,
    shallowEqual
  );

  const loadingInstanceSucceeded = useAppSelector(
    selectTextToSpeechInstanceLoaded
  );

  // Ref used to store our Filtered models.
  const filteredCustomModelsRef = useRef();

  // Set the field value based on selection
  const handleLanguageSelection = useCallback(
    selectedItem => {
      const field =
        bandPreference === NARROWBAND || bandPreference === TELEPHONY
          ? FIELDS.STT.NARROWBAND.LANGUAGE_CUSTOMIZATION_ID
          : FIELDS.STT.BROADBAND.LANGUAGE_CUSTOMIZATION_ID;
      updateFieldValue(field, selectedItem.customization_id);
    },
    [bandPreference, updateFieldValue]
  );
  const handleAcousticSelection = useCallback(
    selectedItem => {
      const field =
        bandPreference === NARROWBAND || bandPreference === TELEPHONY
          ? FIELDS.STT.NARROWBAND.ACCOUSTIC_CUSTOMIZATION_ID
          : FIELDS.STT.BROADBAND.ACCOUSTIC_CUSTOMIZATION_ID;
      updateFieldValue(field, selectedItem.customization_id);
    },
    [bandPreference, updateFieldValue]
  );

  useEffect(() => {
    if (bandPreference === NARROWBAND || bandPreference === TELEPHONY) {
      setCustomAcousticField(FIELDS.STT.NARROWBAND.ACCOUSTIC_CUSTOMIZATION_ID);
      setCustomLanguageField(FIELDS.STT.NARROWBAND.LANGUAGE_CUSTOMIZATION_ID);
    }

    if (bandPreference === BROADBAND) {
      setCustomAcousticField(FIELDS.STT.BROADBAND.ACCOUSTIC_CUSTOMIZATION_ID);
      setCustomLanguageField(FIELDS.STT.BROADBAND.LANGUAGE_CUSTOMIZATION_ID);
    }
  }, [bandPreference, getFieldValue]);

  // When the sttModel or InstanceId changes, update the Filtered language Models.
  useEffect(() => {
    if (sttInstanceId || sttInstanceURl) {
      const sttModel =
        bandPreference === BROADBAND ? sttBroadbandModel : sttNarrowbandModel;
      if (sttModel) {
        filteredCustomModelsRef.current = {
          language: allCustomLanguageModels.filter(
            item => item.base_model_name === sttModel
          ),
          acoustic: allCustomAcousticModels.filter(
            item => item.base_model_name === sttModel
          )
        } as any;
        if (
          !_.isNil(filteredCustomModelsRef.current) &&
          (filteredCustomModelsRef.current as any).language.length === 0
        ) {
          if (getFieldValue(customLanguageField)) {
            // If this field is set, clear it, otherwise leave alone.
            // because if it is undefined, we don't need to set it, causes
            // unnecessary save problems.
            updateFieldValue(customLanguageField, null);
          }
        }
        if (
          !_.isNil(filteredCustomModelsRef.current) &&
          (filteredCustomModelsRef.current as any).acoustic.length === 0
        ) {
          // If this field is set, clear it, otherwise leave alone.
          // because if it is undefined, we don't need to set it, causes
          // unnecessary save problems.
          if (getFieldValue(customAcousticField)) {
            updateFieldValue(customAcousticField, null);
          }
        }
      }
    }
  }, [
    sttNarrowbandModel,
    sttBroadbandModel,
    sttInstanceId,
    allCustomLanguageModels,
    allCustomAcousticModels,
    customLanguageField,
    bandPreference,
    customAcousticField,
    getFieldValue,
    updateFieldValue,
    sttInstanceURl
  ]);

  const currentModel: any = filteredCustomModelsRef.current;

  return (
    <>
      {!_.isNil(currentModel) &&
        currentModel.language?.length > 0 &&
        loadingInstanceSucceeded && (
          <>
            <div className="TabContent__spacer16" />
            <div className="TabContent__field-container">
              <h5>
                <FormattedMessage {...messages.sttCustomLanguageModelLabel} />
              </h5>
              <p className="TabContent__inner-body">
                <FormattedMessage {...messages.customModelHelperText} />
              </p>
              <div className="TabContent__spacer16" />
              <div className="Dropdown__filtered-selector">
                {(bandPreference === NARROWBAND ||
                  bandPreference === TELEPHONY) && (
                  <ControlledDropdownSelector
                    id="custom-language-models-narrow"
                    customizationIdField={true}
                    enableNone={true}
                    labelText={t(messages.sttCustomLanguageModelLabel)}
                    field={FIELDS.STT.NARROWBAND.LANGUAGE_CUSTOMIZATION_ID}
                    displayProp="name"
                    itemProp="customization_id"
                    items={currentModel.language}
                    onChange={handleLanguageSelection}
                    instanceLoaded={true}
                  />
                )}
                {bandPreference === BROADBAND && (
                  <ControlledDropdownSelector
                    id="custom-language-models-broad"
                    customizationIdField={true}
                    enableNone={true}
                    labelText={t(messages.sttCustomLanguageModelLabel)}
                    field={FIELDS.STT.BROADBAND.LANGUAGE_CUSTOMIZATION_ID}
                    displayProp="name"
                    itemProp="customization_id"
                    items={currentModel.language}
                    onChange={handleLanguageSelection}
                    instanceLoaded={true}
                  />
                )}
              </div>
            </div>
          </>
        )}
      {!_.isNil(currentModel) &&
        currentModel.acoustic?.length > 0 &&
        loadingInstanceSucceeded && (
          <>
            <div className="TabContent__spacer16" />
            <div className="TabContent__field-container">
              <h5>
                <FormattedMessage {...messages.sttCustomAcousticModelLabel} />
              </h5>
              <p className="TabContent__inner-body">
                <FormattedMessage {...messages.customModelHelperText} />
              </p>
              <div className="TabContent__spacer16" />
              <div className="Dropdown__filtered-selector">
                {bandPreference === NARROWBAND && (
                  <ControlledDropdownSelector
                    id="custom-acoustic-models-narrow"
                    labelText={t(messages.sttCustomAcousticModelLabel)}
                    customizationIdField={true}
                    enableNone={true}
                    displayProp="name"
                    itemProp="customization_id"
                    field={FIELDS.STT.NARROWBAND.ACCOUSTIC_CUSTOMIZATION_ID}
                    items={currentModel.acoustic}
                    onChange={handleAcousticSelection}
                    instanceLoaded={true}
                  />
                )}
                {bandPreference === BROADBAND && (
                  <ControlledDropdownSelector
                    id="custom-acoustic-models-broad"
                    labelText={t(messages.sttCustomAcousticModelLabel)}
                    customizationIdField={true}
                    enableNone={true}
                    displayProp="name"
                    itemProp="customization_id"
                    field={FIELDS.STT.BROADBAND.ACCOUSTIC_CUSTOMIZATION_ID}
                    items={currentModel.acoustic}
                    onChange={handleAcousticSelection}
                    instanceLoaded={true}
                  />
                )}
              </div>
            </div>
          </>
        )}
    </>
  );
};
